import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

// Mismo marco que la confirmación de borrar una plantilla: icono, texto y fila
// de botones centrada.
import {
  ActionButton,
  ButtonRow,
  Container,
  ContainerLoader,
  ContainerText,
  ModalContainerStyled,
  Text,
  Title,
} from '../DeleteRoutePresetModal/styles';
import { Loader } from '../../../inputs/Loader';
import { AlertIcon } from '../../../../assets/icons/AlertIcon';

export interface UndoSegmentStopModalData {
  onConfirm: () => Promise<void>;
}

interface ModalProps {
  onClickClose: () => void;
  data?: UndoSegmentStopModalData;
}

/**
 * Confirmación de «Deshacer» una parada registrada (spec 2026-10-06 § 6.5).
 *
 * Se confirma porque vuelve a juntar las piezas en el tramo original y suelta
 * los avisos que se ligaron solos al registrarla. El servidor solo lo acepta
 * si nadie ha tocado las piezas después; si no, el toast del hook lo explica y
 * el modal se cierra igual.
 */
export const UndoSegmentStopModal = ({
  onClickClose,
  data,
}: ModalProps): JSX.Element => {
  const { t } = useTranslation(['pages', 'common']);
  const theme = useTheme();
  const [isLoading, setIsLoading] = useState(false);

  const handleConfirm = async () => {
    if (!data?.onConfirm) return;
    setIsLoading(true);
    try {
      await data.onConfirm();
      onClickClose();
    } catch {
      setIsLoading(false);
    }
  };

  return (
    <ModalContainerStyled
      width={50}
      onClickClose={onClickClose}
      fill={theme.colors.black}
    >
      {isLoading && (
        <ContainerLoader>
          <Loader />
        </ContainerLoader>
      )}
      <Container
        role="dialog"
        aria-modal="true"
        aria-label={t('pages:surveillance.registerStop.undoTitle')}
        isLoading={isLoading}
      >
        <AlertIcon />
        <ContainerText>
          <Title variant="semiBold">
            {t('pages:surveillance.registerStop.undoTitle')}
          </Title>
          <Text size="M" colorText="greyDark">
            {t('pages:surveillance.registerStop.undoConfirm')}
          </Text>
        </ContainerText>
        <ButtonRow>
          <ActionButton
            type="button"
            text={t('common:buttons.cancel')}
            variant="secondary"
            onClick={onClickClose}
            disabled={isLoading}
          />
          <ActionButton
            type="button"
            text={t('pages:surveillance.registerStop.undoLink')}
            onClick={handleConfirm}
            disabled={isLoading}
            data-testid="undo-stop-confirm"
          />
        </ButtonRow>
      </Container>
    </ModalContainerStyled>
  );
};

export default UndoSegmentStopModal;
